// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import api from '@crm-src/lib/api'

interface Activity {
  id: string
  activity_type: string
  activity_subtype?: string
  title: string
  description?: string
  outcome?: string
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show'
  duration_minutes?: number
  scheduled_at?: string
  completed_at?: string
  due_date?: string
  communication_direction?: 'inbound' | 'outbound'
  communication_method?: string
  location?: string
  call_result?: string
  email_subject?: string
  email_opened?: boolean
  email_clicked?: boolean
  email_replied?: boolean
  engagement_score?: number
  follow_up_required?: boolean
  next_follow_up_date?: string

  // Task-specific fields
  task_priority?: 'low' | 'medium' | 'high' | 'urgent'
  task_completed?: boolean
  task_completed_at?: string

  // Relations
  subject?: {
    id: number
    type: string
    name: string
  }
  deal?: {
    id: number
    name: string
    amount: number
    stage: string
    status: string
  }
  user: {
    id: number
    name: string
  }
  assigned_to?: {
    id: number
    name: string
  }

  // Timestamps
  created_at: string
  updated_at: string
}

interface TaskStats {
  total: number
  completed: number
  pending: number
  overdue: number
  completion_rate: number
}

interface ActivitiesState {
  activities: Activity[]
  tasks: Activity[]
  stats: TaskStats | null
  upcomingTasks: Activity[]
  overdueTasks: Activity[]
  calendarData: { date: string; activities: Activity[] }[]
  isLoading: boolean
  error: string | null
  lastFetched: number | null
  pagination: {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
    has_next_page: boolean
    has_prev_page: boolean
  } | null
}

const initialState: ActivitiesState = {
  activities: [],
  tasks: [],
  stats: null,
  upcomingTasks: [],
  overdueTasks: [],
  calendarData: [],
  isLoading: false,
  error: null,
  lastFetched: null,
  pagination: null,
}

export const fetchUpcomingTasks = createAsyncThunk(
  'activities/fetchUpcomingTasks',
  async (params: { limit?: number; my_activities?: boolean } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/tasks', { params })
      // For now return empty array since v1 API is a stub
      return []
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || 'Failed to fetch upcoming tasks')
    }
  }
)

export const fetchOverdueTasks = createAsyncThunk(
  'activities/fetchOverdueTasks',
  async (params: { my_activities?: boolean } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/tasks', { params })
      // For now return empty array since v1 API is a stub
      return []
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || 'Failed to fetch overdue tasks')
    }
  }
)

const activitiesSlice = createSlice({
  name: 'activities',
  initialState,
  reducers: {
    clearActivities: (state) => {
      state.activities = []
      state.tasks = []
      state.upcomingTasks = []
      state.overdueTasks = []
      state.calendarData = []
      state.stats = null
      state.pagination = null
      state.lastFetched = null
      state.error = null
    },

    clearCache: (state) => {
      state.lastFetched = null
    },

    clearError: (state) => {
      state.error = null
    },

    // Optimistic updates
    optimisticToggleComplete: (state, action: PayloadAction<string>) => {
      const activityId = action.payload
      const updateActivity = (activity: Activity) => {
        if (activity.id === activityId) {
          activity.status = activity.status === 'completed' ? 'scheduled' : 'completed'
          activity.task_completed = activity.status === 'completed'
          activity.completed_at = activity.status === 'completed' ? new Date().toISOString() : undefined
        }
      }

      state.activities.forEach(updateActivity)
      state.tasks.forEach(updateActivity)
      state.upcomingTasks.forEach(updateActivity)
      state.overdueTasks.forEach(updateActivity)
    },

    // Real-time updates (for websocket integration)
    activityCreated: (state, action: PayloadAction<Activity>) => {
      state.activities.unshift(action.payload)
      if (action.payload.activity_type === 'task') {
        state.tasks.unshift(action.payload)
      }
    },

    activityUpdated: (state, action: PayloadAction<Activity>) => {
      const updatedActivity = action.payload
      if (!updatedActivity || !updatedActivity.id) {
        console.warn('⚠️ Activity updated but payload is missing or invalid:', updatedActivity)
        return
      }

      const updateInArray = (array: Activity[]) => {
        const index = array.findIndex(a => a.id === updatedActivity.id)
        if (index !== -1) {
          array[index] = updatedActivity
        }
      }

      updateInArray(state.activities)
      updateInArray(state.tasks)
      updateInArray(state.upcomingTasks)
      updateInArray(state.overdueTasks)
    },

    activityDeleted: (state, action: PayloadAction<string>) => {
      const activityId = action.payload
      const removeFromArray = (array: Activity[]) => array.filter(a => a.id !== activityId)

      state.activities = removeFromArray(state.activities)
      state.tasks = removeFromArray(state.tasks)
      state.upcomingTasks = removeFromArray(state.upcomingTasks)
      state.overdueTasks = removeFromArray(state.overdueTasks)
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch upcoming tasks
      .addCase(fetchUpcomingTasks.fulfilled, (state, action) => {
        state.upcomingTasks = action.payload
      })
      .addCase(fetchUpcomingTasks.rejected, (state, action) => {
        state.error = action.payload as string
      })

      // Fetch overdue tasks
      .addCase(fetchOverdueTasks.fulfilled, (state, action) => {
        state.overdueTasks = action.payload
      })
      .addCase(fetchOverdueTasks.rejected, (state, action) => {
        state.error = action.payload as string
      })
  },
})

export const {
  clearActivities,
  clearCache,
  clearError,
  optimisticToggleComplete,
  activityCreated,
  activityUpdated,
  activityDeleted
} = activitiesSlice.actions

export default activitiesSlice.reducer
